import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        origin: window.location.origin,
      });
      setSent(true);
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 py-20" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_beyond-stuck/artifacts/4bqnieq1_Company%20Logo.jpeg"
              alt="Logo"
              className="h-14 w-14 mx-auto mb-4 rounded-sm"
            />
          </Link>
          <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-2">De Nicola Consulting Co.</p>
          <h1 className="font-lora text-2xl text-navy font-normal">Reset Password</h1>
          <div className="w-8 h-px bg-gold mx-auto mt-4"></div>
        </div>

        {sent ? (
          <div className="text-center" data-testid="reset-sent-message">
            <CheckCircle size={36} className="text-green-600 mx-auto mb-4" />
            <h2 className="font-lora text-navy text-lg font-normal mb-3">Check Your Inbox</h2>
            <p className="font-serif text-navy/60 text-sm leading-relaxed mb-6">
              If an account exists for <span className="text-navy font-semibold">{email}</span>, you will receive a password reset link within a few minutes. The link expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="text-navy/50 font-serif text-sm hover:text-navy transition-colors underline underline-offset-2"
              data-testid="back-to-login-link"
            >
              Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <p className="font-serif text-navy/50 text-sm text-center mb-8 leading-relaxed">
              Enter the email address associated with your account and we will send you a reset link.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy transition-colors"
                  placeholder="your@email.com"
                  data-testid="forgot-email-input"
                />
              </div>

              {error && <p className="text-red-600 font-serif text-sm" data-testid="forgot-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-navy text-ivory py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
                data-testid="forgot-submit-btn"
              >
                {loading ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : "Send Reset Link"}
              </button>
            </form>

            <p className="text-center text-navy/50 font-serif text-sm mt-6">
              <Link to="/login" className="text-navy hover:text-gold transition-colors underline" data-testid="back-to-login-link">
                Back to Sign In
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
